/**
 * Momentum: the Next Steps from every Unfolding, gathered into month folders.
 *
 * Items come from GET /api/journals/momentum — the same `actions` rows the journal's
 * Next Steps card shows, never a copy — so completing one here is the same write as
 * completing it in its journal.
 */

export interface MomentumItem {
  id: string;
  journalId: string;
  text: string;
  completed: boolean;
  journalTitle: string;
  sourceTitle: string | null;
  /** The source Unfolding's date; an item belongs to that Unfolding's month. */
  journalCreatedAt: string;
}

export interface MomentumMonth {
  /** "yyyy-MM" (month 1-based), stable across renders — used for the URL and React keys. */
  key: string;
  year: number;
  /** 0-based, as Date#getMonth. */
  month: number;
  isCurrent: boolean;
  items: MomentumItem[];
  done: number;
}

export interface MomentumYear {
  year: number;
  months: MomentumMonth[];
}

export function monthKey(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}`;
}

/**
 * Group items by their Unfolding's month, in the viewer's local time (the same way the
 * Unfoldings calendar places entries).
 *
 * The current year shows January through the current month, empty or not; future months
 * are never shown. Past years show only months that hold items. Years run newest first,
 * months within a year run January onward, like a row of folders on a shelf.
 */
export function groupMomentum(items: MomentumItem[], now: Date = new Date()): MomentumYear[] {
  const byKey = new Map<string, MomentumItem[]>();
  for (const item of items) {
    const d = new Date(item.journalCreatedAt);
    if (Number.isNaN(d.getTime())) continue;
    const key = monthKey(d.getFullYear(), d.getMonth());
    const list = byKey.get(key);
    if (list) list.push(item);
    else byKey.set(key, [item]);
  }

  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth();
  const years = new Set<number>([thisYear]);
  for (const key of byKey.keys()) {
    const y = Number(key.slice(0, 4));
    // An Unfolding dated in the future (clock skew) would otherwise open a folder we
    // promise never to show; it lands in the current month instead.
    if (y <= thisYear) years.add(y);
  }

  const toMonth = (year: number, month: number, extra: MomentumItem[] = []): MomentumMonth => {
    const monthItems = [...(byKey.get(monthKey(year, month)) ?? []), ...extra];
    return {
      key: monthKey(year, month),
      year,
      month,
      isCurrent: year === thisYear && month === thisMonth,
      items: monthItems,
      done: monthItems.filter((i) => i.completed).length,
    };
  };

  const future: MomentumItem[] = [];
  for (const [key, list] of byKey) {
    const y = Number(key.slice(0, 4));
    const m = Number(key.slice(5, 7)) - 1;
    if (y > thisYear || (y === thisYear && m > thisMonth)) future.push(...list);
  }

  return [...years]
    .sort((a, b) => b - a)
    .map((year) => {
      if (year === thisYear) {
        const months = Array.from({ length: thisMonth + 1 }, (_, m) =>
          toMonth(year, m, m === thisMonth ? future : []),
        );
        return { year, months };
      }
      const months = Array.from({ length: 12 }, (_, m) => m)
        .filter((m) => byKey.has(monthKey(year, m)))
        .map((m) => toMonth(year, m));
      return { year, months };
    });
}

/**
 * Open steps first, then completed ones, each keeping their original order. Applied when
 * a folder opens (not on every toggle) so a step never jumps out from under the pointer.
 */
export function orderForOpening(items: MomentumItem[]): string[] {
  return [...items.filter((i) => !i.completed), ...items.filter((i) => i.completed)].map((i) => i.id);
}

/** Apply a saved open-time order; items that arrived since go at the end. */
export function applyOrder(items: MomentumItem[], order: string[] | undefined): MomentumItem[] {
  if (!order) return items;
  const rank = new Map(order.map((id, i) => [id, i]));
  const at = (item: MomentumItem, i: number) => rank.get(item.id) ?? order.length + i;
  return items
    .map((item, i) => ({ item, r: at(item, i) }))
    .sort((a, b) => a.r - b.r)
    .map(({ item }) => item);
}

/** A month's quiet progress line: "3 of 7 done", or a whole-month finish. */
export function progressLabel(done: number, total: number): string {
  if (total === 0) return "";
  if (done === 0) return total === 1 ? "1 step" : `${total} steps`;
  if (done === total) return total === 1 ? "Done" : `All ${total} done`;
  return `${done} of ${total} done`;
}
